/**
 * BCR Personal Loan Calculator
 * Manages two noUiSlider instances for loan amount and repayment period.
 */

class BcrCalculator {
    constructor(config) {
        this.config = config;
        this.sliders = {};
        this.state = {
            activePipDownPayment: null,
            activePipPayback: null,
        };
    }

    /**
     * Initializes the calculator components
     */
    init() {
        const form = document.getElementById('sendCreditRequest');
        if (!form) return;

        this._initSliders();
        this._initPopovers();
        this._attachFormListeners();
        this._syncBannerHeightToAdImage();
    }

    _initSliders() {
        this._createDownPaymentSlider();
        this._createPaybackSlider();
    }

    /**
     * Handles the loan amount selection with a dynamic barrier logic
     */
    _createDownPaymentSlider() {
        const sliderEl = document.getElementById('downpayment-slider');
        if (!sliderEl || typeof noUiSlider === 'undefined') return;

        const { maxAmount, maxCreditAmount, pipsCreditValues } = this.config;
        
        // Define the upper limit based on the ad price or the general max amount
        const barrier = (maxCreditAmount != null ? maxCreditAmount : maxAmount);
        
        const pipsValues = pipsCreditValues && pipsCreditValues.length 
            ? pipsCreditValues 
            : [0, 40000, 80000, 120000, 160000, maxAmount];

        this.sliders.downPayment = noUiSlider.create(sliderEl, {
            start: barrier,
            range: { min: 0, max: maxAmount },
            step: 1,
            connect: 'lower',
            pips: {
                mode: 'values',
                values: pipsValues,
                density: 100,
                format: {
                    to: value => new Intl.NumberFormat('de-DE').format(value),
                    from: value => parseFloat(String(value).replace(/\./g, '').replace(',', '.')),
                },
            },
        });

        this._populateHighlightBlocks(this.sliders.downPayment, document.querySelector('.highlight-blocks'));

        if (barrier < maxAmount) {
            this._creditBarrier = barrier;
            const wrap = sliderEl.closest('.bcr-credit-slider-wrap');
            if (wrap) {
                this._createCreditLimitMarkerAndOverlay(wrap, barrier, maxAmount);
            }
        } else {
            this._creditBarrier = null;
        }

        this.sliders.downPayment.on('slide', (values, handle, unencoded) => {
            const val = parseFloat(unencoded[0]);
            if (val > barrier) this.sliders.downPayment.set(barrier);
        });

        this.sliders.downPayment.on('update', (values) => {
            let val = Math.round(parseFloat(values[0]));
            if (val > barrier) {
                this.sliders.downPayment.set(barrier);
                val = barrier;
            }
            const formatted = new Intl.NumberFormat('de-DE').format(val) + (this.config.currency ? ' ' + this.config.currency : '');
            
            if (this.creditLimitMarkerLabel) {
                this.creditLimitMarkerLabel.classList.toggle('at-limit', val >= barrier);
            }
            
            // Visual update for the closest pip
            this.state.activePipDownPayment = this._updateActivePip(sliderEl, values, this.state.activePipDownPayment);
            
            // Update all UI labels linked to credit value
            document.querySelectorAll('.downpayment-value').forEach(el => el.textContent = formatted);
            document.querySelectorAll('#result-credit-value').forEach(el => el.textContent = new Intl.NumberFormat('de-DE').format(val));
            
            // Synchronize with the hidden form input
            const input = document.getElementById('credit_value_input');
            if (input) input.value = val;
            
            this._updateActiveHighlightBlock(this.sliders.downPayment, document.querySelector('.highlight-blocks'));
        });
    }

    /**
     * handles the number of months for the loan
     */
    _createPaybackSlider() {
        const sliderEl = document.getElementById('payback-slider');
        if (!sliderEl || typeof noUiSlider === 'undefined') return;

        const maxMonths = this.config.maxMonths || 60;
        const pipsValues = (this.config.pipsPaybackValues && this.config.pipsPaybackValues.length)
            ? this.config.pipsPaybackValues
            : [12, 24, 36, 48, 60];

        this.sliders.payback = noUiSlider.create(sliderEl, {
            start: maxMonths,
            range: { min: 12, max: maxMonths },
            step: pipsValues[1] - pipsValues[0] || 12,
            connect: 'lower',
            pips: {
                mode: 'values',
                values: pipsValues,
                density: 100,
                format: { to: value => value, from: value => Number(value) },
            },
        });

        this.sliders.payback.on('update', (values) => {
            const val = Math.round(parseFloat(values[0]));
            this.state.activePipPayback = this._updateActivePip(sliderEl, values, this.state.activePipPayback);
            
            // Update UI labels and hidden input
            document.querySelectorAll('.payback-value').forEach(el => el.textContent = val);
            document.querySelectorAll('#result-payback').forEach(el => el.textContent = val);
            
            const input = document.getElementById('number_of_payments_input');
            if (input) input.value = val;
        });
    }

    /**
     * Updates the active class on the pip label: highlight the pip for the block we're in
     */
    _updateActivePip(sliderElement, values, activePip, activePipClass = 'active-pip') {
        if (!sliderElement) return null;
        if (activePip) activePip.classList.remove(activePipClass);
        
        let currentValue = parseFloat(values[0]);
        const isCreditSlider = sliderElement.id === 'downpayment-slider';
        const pipsValues = (this.sliders.downPayment && isCreditSlider)
            ? this.sliders.downPayment.options.pips.values
            : (this.sliders.payback && !isCreditSlider) ? this.sliders.payback.options.pips.values : null;
        if (!pipsValues || !pipsValues.length) return null;
        if (isCreditSlider && this._creditBarrier != null) {
            currentValue = Math.min(currentValue, this._creditBarrier);
        }
        const activeIndex = this._getActiveBlockIndex(pipsValues, currentValue);
        const targetValue = pipsValues[activeIndex];
        let newActivePip = sliderElement.querySelector(`.noUi-value[data-value="${targetValue}"]`);
        if (!newActivePip) {
            const pipElements = sliderElement.querySelectorAll('.noUi-value');
            let closestElement = null;
            let minDiff = Infinity;
            pipElements.forEach(el => {
                const dataVal = el.getAttribute('data-value');
                if (dataVal !== null) {
                    const diff = Math.abs(parseFloat(dataVal) - targetValue);
                    if (diff < minDiff) {
                        minDiff = diff;
                        closestElement = el;
                    }
                }
            });
            newActivePip = closestElement;
        }
        if (newActivePip) newActivePip.classList.add(activePipClass);
        return newActivePip;
    }

    /**
     * Generates visual background blocks based on pips
     */
    _populateHighlightBlocks(slider, container) {
        if (!slider || !container) return;
        const pipsValues = slider.options.pips.values;
        container.innerHTML = '';
        pipsValues.forEach((_, index) => {
            const block = document.createElement('div');
            block.className = 'highlight-block';
            if (index === 0) block.classList.add('first-highlight-block');
            if (index === pipsValues.length - 1) block.classList.add('last-highlight-block');
            container.appendChild(block);
        });
        this._updateActiveHighlightBlock(slider, container);
    }

    /**
     * Returns the block index  that contains the value
     */
    _getActiveBlockIndex(pipsValues, value) {
        if (!pipsValues || !pipsValues.length) return 0;
        const val = Math.round(value);
        for (let i = 0; i < pipsValues.length; i++) {
            const left = i === 0 ? pipsValues[0] : Math.round((pipsValues[i - 1] + pipsValues[i]) / 2);
            const right = i === pipsValues.length - 1
                ? pipsValues[i] + 1
                : Math.round((pipsValues[i] + pipsValues[i + 1]) / 2);
            if (val >= left && val < right) return i;
        }
        return pipsValues.length - 1;
    }

    /**
     * Highlights the block that contains the current slider value
     */
    _updateActiveHighlightBlock(slider, container) {
        if (!slider || !container) return;
        const highlightBlocks = container.querySelectorAll('.highlight-block');
        const pipsValues = slider.options.pips.values;
        let sliderValue = parseFloat(slider.get());
        if (this._creditBarrier != null) {
            sliderValue = Math.min(sliderValue, this._creditBarrier);
        }
        const activeIndex = this._getActiveBlockIndex(pipsValues, sliderValue);

        if (highlightBlocks[activeIndex]) {
            highlightBlocks.forEach(block => block.classList.remove('active'));
            highlightBlocks[activeIndex].classList.add('active');
        }
    }

    /**
     * disabled zone to the right
     */
    _createCreditLimitMarkerAndOverlay(wrapper, barrier, maxAmount) {
        const leftPct = (barrier / maxAmount) * 100;
        const label = this.config.creditLimitMarkerLabel || '';
        const formattedBarrier = new Intl.NumberFormat('de-DE').format(barrier);

        const marker = document.createElement('div');
        marker.className = 'bcr-credit-limit-marker';
        marker.style.left = leftPct + '%';
        if (label) {
            const labelEl = document.createElement('span');
            labelEl.className = 'bcr-credit-limit-marker-label';
            labelEl.textContent = label + ' (' + formattedBarrier + ' RON)';
            marker.appendChild(labelEl);
            this.creditLimitMarkerLabel = labelEl;
        }

        const disabledZone = document.createElement('div');
        disabledZone.className = 'bcr-credit-slider-disabled-zone';
        disabledZone.style.left = leftPct + '%';

        wrapper.appendChild(marker);
        wrapper.appendChild(disabledZone);

        this._creditLimitMarker = marker;
        this._creditLimitMarkerLeftPct = leftPct;
        this._creditLimitMarkerWrapper = wrapper;
        this._updateMarkerLabelNearEdges();

        const ro = new ResizeObserver(() => this._updateMarkerLabelNearEdges());
        ro.observe(wrapper);
        this._creditLimitResizeObserver = ro;
    }

    /**
     * when the marker is near the edge, the label might overflow the screen
     */
    _updateMarkerLabelNearEdges() {
        const marker = this._creditLimitMarker;
        const wrapper = this._creditLimitMarkerWrapper;
        const leftPct = this._creditLimitMarkerLeftPct;
        if (!marker || !wrapper || leftPct == null) return;
        const labelEl = marker.querySelector('.bcr-credit-limit-marker-label');
        if (!labelEl) return;

        // Edge correction only on mobile
        const isMobile = window.matchMedia('(max-width: 575.98px)').matches;
        if (!isMobile) {
            marker.classList.remove('bcr-credit-limit-marker--near-right');
            marker.classList.remove('bcr-credit-limit-marker--near-left');
            return;
        }

        const wrapW = wrapper.offsetWidth;
        const labelW = labelEl.offsetWidth;
        const markerCenterX = (leftPct / 100) * wrapW;
        const labelLeftEdge = markerCenterX - labelW / 2;
        const labelRightEdge = markerCenterX + labelW / 2;
        const edgePadding = 8;
        const nearRight = labelRightEdge > wrapW - edgePadding;
        const nearLeft = labelLeftEdge < edgePadding;
        marker.classList.toggle('bcr-credit-limit-marker--near-right', nearRight && !nearLeft);
        marker.classList.toggle('bcr-credit-limit-marker--near-left', nearLeft && !nearRight);
    }

    _initPopovers() {
        const popoverTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="popover"]'));
        popoverTriggerList.forEach(el => new bootstrap.Popover(el));
    }

    /**
     * sync before submission
     */
    _attachFormListeners() {
        const form = document.getElementById('sendCreditRequest');
        if (!form) return;

        form.addEventListener('submit', () => {
            const creditInput = document.getElementById('credit_value_input');
            const paybackInput = document.getElementById('number_of_payments_input');
            
            if (this.sliders.downPayment) creditInput.value = Math.round(parseFloat(this.sliders.downPayment.get()));
            if (this.sliders.payback) paybackInput.value = Math.round(parseFloat(this.sliders.payback.get()));
            
            // Prevent double submission
            form.querySelector('button[type="submit"]').disabled = true;
        });
    }

    /**
     * On desktop sets the BCR promo banner height to match the ad image height.
     */
    _syncBannerHeightToAdImage() {
        const section = document.querySelector('.calculator-section');
        const adImg = section?.querySelector('.calculator-section-left .leasing-car-summary img');
        const desktopBanner = document.querySelector('.calculator-section-right .bcr-promo-banner-desktop');
        if (!adImg || !desktopBanner) return;

        const applyHeight = () => {
            if (window.innerWidth < 1200) {
                desktopBanner.style.height = '';
                desktopBanner.style.minHeight = '';
                return;
            }
            const imgHeight = adImg.getBoundingClientRect().height;
            if (imgHeight > 0) {
                // Only enforce a minimum height so the banner aligns with a tall
                // ad image, but can still grow for its own content when the image
                // is short (otherwise the overflow-hidden banner truncates the text).
                desktopBanner.style.height = '';
                desktopBanner.style.minHeight = `${imgHeight}px`;
            }
        };

        applyHeight();
        window.addEventListener('resize', applyHeight);
        if (adImg.complete) {
            applyHeight();
        } else {
            adImg.addEventListener('load', applyHeight);
        }
    }
}

/**
 * Detects the config element and runs the calculator instance
 */
(function initBcrCalculator() {
    function run() {
        const configEl = document.getElementById('bcr-calculator-config');
        if (configEl) {
            try {
                const config = JSON.parse(configEl.textContent);
                const calculator = new BcrCalculator(config);
                calculator.init();
            } catch (e) {
            }
        }
    }
    
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run);
    } else {
        run();
    }
})();
